// ---------------------------------------------------------------------------
// JOB ALERTS / SAVED SEARCHES — contractor dashboard panel.
// ---------------------------------------------------------------------------
// Create, edit, pause and delete standing filter sets. Every alert is emailed
// by the daily `job-alerts-digest` cron and carries a one-click unsubscribe
// link, which simply flips `active` off — the row (and the contractor's
// account mail) is never touched.
//
// The filter set is intentionally identical to the marketplace browse filters
// (trade, state, city, residential/commercial, minimum budget). Radius is NOT
// offered: listings carry no geocode, so a mileage alert would be fabricated.
// ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, Plus, Trash2, Pause, Play, Loader2, Check } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { TRADES, US_STATES, tradeLabel } from '@/data/constants';
import {
  SavedSearch, SavedSearchDraft, EMPTY_DRAFT, MAX_SAVED_SEARCHES, MIN_BUDGET_STEPS,
  listSavedSearches, createSavedSearch, updateSavedSearch, deleteSavedSearch,
  validateDraft, previewMatchCount, describeSearch,
} from '@/lib/savedSearches';

const JobAlertsPanel: React.FC = () => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [rows, setRows] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SavedSearchDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  const reload = useCallback(async () => {
    setRows(await listSavedSearches(profile?.id));
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => { reload(); }, [reload]);

  // Live "this filter set matches N projects right now" — run against the same
  // public view and listing_open gate the digest uses, so it is a real answer.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const id = window.setTimeout(() => {
      previewMatchCount(draft).then((n) => { if (!cancelled) setPreview(n); });
    }, 350);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [open, draft]);

  const toggleTrade = (slug: string) =>
    setDraft((d) => ({
      ...d,
      trades: d.trades.includes(slug) ? d.trades.filter((s) => s !== slug) : [...d.trades, slug],
    }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    const msg = validateDraft(draft, lang);
    if (msg) { setError(msg); return; }
    setError(null);
    setSaving(true);
    const err = await createSavedSearch(profile.id, profile.email, lang, draft);
    setSaving(false);
    if (err) { setError(err.message); return; }
    setDraft(EMPTY_DRAFT);
    setOpen(false);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 4000);
    reload();
  };

  const atLimit = rows.length >= MAX_SAVED_SEARCHES;

  return (
    <section className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <BellRing className="h-5 w-5 text-orange-500" /> {t('alerts.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{t('alerts.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => { setOpen((v) => !v); setError(null); }}
          disabled={atLimit && !open}
          className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> {open ? t('common.cancel') : t('alerts.new')}
        </button>
      </div>

      {saved && (
        <p role="status" className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          <Check className="h-4 w-4" /> {t('alerts.created')}
        </p>
      )}
      {atLimit && !open && (
        <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {t('alerts.limit').replace('{n}', String(MAX_SAVED_SEARCHES))}
        </p>
      )}

      {/* ---- CREATE FORM ---- */}
      {open && (
        <form onSubmit={submit} className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="al-name" className="mb-1.5 block text-sm font-bold text-slate-800">{t('alerts.name')}</label>
              <input
                id="al-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={t('alerts.namePlaceholder')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              />
            </div>

            <div className="sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold text-slate-800">{t('alerts.trades')}</span>
              <div className="flex flex-wrap gap-2">
                {TRADES.map((tr) => {
                  const on = draft.trades.includes(tr.slug);
                  return (
                    <button
                      key={tr.slug}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleTrade(tr.slug)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                        on ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      {tradeLabel(tr.slug, lang)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-slate-500">{t('alerts.tradesHint')}</p>
            </div>

            <div>
              <label htmlFor="al-state" className="mb-1.5 block text-sm font-bold text-slate-800">{t('jobs.filterState')}</label>
              <select
                id="al-state"
                value={draft.state || ''}
                onChange={(e) => setDraft({ ...draft, state: e.target.value || null })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              >
                <option value="">{t('alerts.any')}</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="al-city" className="mb-1.5 block text-sm font-bold text-slate-800">{t('jobs.filterCity')}</label>
              <input
                id="al-city"
                value={draft.city || ''}
                onChange={(e) => setDraft({ ...draft, city: e.target.value || null })}
                placeholder={t('alerts.any')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              />
            </div>

            <div>
              <label htmlFor="al-type" className="mb-1.5 block text-sm font-bold text-slate-800">{t('alerts.projectType')}</label>
              <select
                id="al-type"
                value={draft.project_type || ''}
                onChange={(e) => setDraft({ ...draft, project_type: (e.target.value || null) as SavedSearchDraft['project_type'] })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              >
                <option value="">{t('alerts.any')}</option>
                <option value="residential">{t('alerts.residential')}</option>
                <option value="commercial">{t('alerts.commercial')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="al-budget" className="mb-1.5 block text-sm font-bold text-slate-800">{t('alerts.minBudget')}</label>
              <select
                id="al-budget"
                value={draft.min_budget == null ? '' : String(draft.min_budget)}
                onChange={(e) => setDraft({ ...draft, min_budget: e.target.value ? Number(e.target.value) : null })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              >
                <option value="">{t('alerts.any')}</option>
                {MIN_BUDGET_STEPS.map((n) => (
                  <option key={n} value={n}>{`$${n.toLocaleString('en-US')}+`}</option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">{t('alerts.budgetHint')}</p>
            </div>

            <div>
              <label htmlFor="al-freq" className="mb-1.5 block text-sm font-bold text-slate-800">{t('alerts.frequency')}</label>
              <select
                id="al-freq"
                value={draft.frequency}
                onChange={(e) => setDraft({ ...draft, frequency: e.target.value as 'daily' | 'weekly' })}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
              >
                <option value="daily">{t('alerts.daily')}</option>
                <option value="weekly">{t('alerts.weekly')}</option>
              </select>
            </div>
          </div>

          {preview !== null && (
            <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700" role="status" aria-live="polite">
              {t('alerts.preview').replace('{n}', String(preview))}
            </p>
          )}
          {error && (
            <p role="alert" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t('alerts.save')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700">
              {t('common.cancel')}
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">{t('alerts.emailNote')}</p>
        </form>
      )}

      {/* ---- EXISTING ALERTS ---- */}
      {loading ? (
        <p className="mt-5 text-sm text-slate-500">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        !open && (
          <p className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            {t('alerts.empty')}
          </p>
        )
      ) : (
        <ul className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-col gap-3 border-b border-slate-100 p-4 last:border-0 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-slate-900">{r.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${r.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {r.active ? t('alerts.on') : t('alerts.off')}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                    {r.frequency === 'weekly' ? t('alerts.weekly') : t('alerts.daily')}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {describeSearch(r, lang, (slug) => tradeLabel(slug, lang))}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {r.last_sent_at
                    ? t('alerts.lastSent')
                        .replace('{date}', new Date(r.last_sent_at).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US'))
                        .replace('{n}', String(r.last_match_count))
                    : t('alerts.neverSent')}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => { await updateSavedSearch(r.id, { active: !r.active }); reload(); }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:border-slate-400"
                >
                  {r.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {r.active ? t('alerts.pause') : t('alerts.resume')}
                </button>
                <button
                  type="button"
                  onClick={async () => { await deleteSavedSearch(r.id); reload(); }}
                  aria-label={`${t('alerts.delete')} — ${r.name}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-600 hover:border-red-300 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t('alerts.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default JobAlertsPanel;
